import { emailValidatorNetAPI } from "@services/emailValidatorNetAPI";
import { logger } from "@services/workers/helpers";
import { debounceStorage } from "../..";
import { state, temp, ui } from "../../interfaces/interfaces";
import AddressUtils from "../address";
import type {
	AddressFields,
	AddressPrefix,
	BookingPaymentMethodPrefix,
	CarePlanBankDetails,
	CarePlanBoiler,
	CarePlanOffer,
	CarePlanPaymentMethod,
	CarePlanPlanOptions,
	CarePlanPropertyDetails,
	ContactFields,
	ContactPrefix,
	Prefix,
} from "../BasicTypes";
import type { ReplaceStringInKeys } from "../BasicTypes/utils";
import type { BookingPaymentMethodPaymentMethodUI } from "../ComplexTypes";
import config from "../config";

const namespace = "Validations";

// create minimal version of isEmailValid
export const isEmailValidMinimal = (email: string) => {
	if (email.length < 5) return false;
	return /^\w+([.-]\w+)*@\w+([.-]\w+)*\.\w{2,3}$/.test(email);
};

// create arrow function calling email validation API
const isEmailValidAPI = (email: string) => {
	if (!config.debug) {
		return emailValidatorNetAPI(email)
			.then((response) => {
				if (response) {
					console.log("isEmailValidAPI", response);
					return response;
				}
				throw new Error("Network response was not ok.");
			})
			.then((response) => {
				if (response.status >= 200 && response.status < 300) {
					addValidEmailAddress(email);
					ui.ProblemMsg = `${email} is valid.`;
					ui.ProblemMsgVariant = "success";
					ui.ProblemShowMsg = true;

					return true;
				}
				if (response.status === 119) {
					addValidEmailAddress(email);
					ui.ProblemMsg = `${email} could not be validated, please notify manager to report that email validation API credit is depleted!!!`;
					ui.ProblemMsgVariant = "warning";
					ui.ProblemShowMsg = true;

					return true;
				}
				ui.ProblemMsg = `${email} is not valid.`;
				ui.ProblemMsgVariant = "error";
				ui.ProblemShowMsg = true;
				addInvalidEmailAddress(email);
				return false;
			})
			.catch((error) => {
				console.error("isEmailValidAPI", error);
				ui.ProblemMsg = `${email} not validated. ${error.message}`;
				ui.ProblemMsgVariant = "error";
				ui.ProblemShowMsg = true;
				return true;
			});
	}
	return true;
};

export const isEmailValidForReact = async (
	email: string,
	setter: React.Dispatch<React.SetStateAction<boolean>>,
) => {
	if (isEmailIncludedInValid(email)) {
		setter(true);
		return;
	}
	if (isEmailIncludedInInvalid(email)) {
		setter(false);
		return;
	}
	if (isEmailValidMinimal(email)) {
		logger.debug(
			`${namespace}EmailValidator`,
			`isEmailValid: ${email} is valid`,
		);
		const callback = async () => {
			const valid = await isEmailValidAPI(email);
			setter(valid);
		};
		debounceStorage.addOrExecute({
			name: "isEmailValidAPI",
			callback,
			delay: 1000,
		});
		return;
	}
	logger.debug(
		`${namespace}EmailValidator`,
		`isEmailValid: ${email} is invalid`,
	);
	setter(false);
};

export const isEmailValid = (email: string) => {
	if (isEmailIncludedInValid(email)) {
		return true;
	}
	if (isEmailIncludedInInvalid(email)) {
		return false;
	}
	if (isEmailValidMinimal(email) && isEmailValidAPI(email)) {
		logger.debug(
			`${namespace}EmailValidator`,
			`isEmailValid: ${email} is valid`,
		);
		return true;
	}
	logger.debug(
		`${namespace}EmailValidator`,
		`isEmailValid: ${email} is invalid`,
	);
	return false;
};

// add valid email address to array if it is not already included
export const addValidEmailAddress = (email: string) => {
	if (!isEmailIncludedInValid(email)) {
		const validEmailAddresses = [...state.ValidEmailAddresses];
		validEmailAddresses.push(email);
		state.ValidEmailAddresses = validEmailAddresses;
		logger.debug(
			`${namespace}EmailValidator`,
			`Valid email address added: ${email}`,
		);
	}
};

// check if email is included in the valid email addresses array
export const isEmailIncludedInValid = (email: string) => {
	const validEmailAddresses = state.ValidEmailAddresses;
	if (validEmailAddresses?.includes(email)) {
		logger.debug(
			`${namespace}EmailValidator`,
			`Valid email address already included: ${email}`,
		);
		return true;
	}
	return false;
};
// add invalid email address to array if it is not already included
export const addInvalidEmailAddress = (email: string) => {
	if (!isEmailIncludedInInvalid(email)) {
		logger.debug(
			`${namespace}EmailValidator`,
			`Invalid email address added: ${email}`,
		);
		const emailArray = [...state.InvalidEmailAddresses];
		emailArray.push(email);
		state.InvalidEmailAddresses = emailArray;
	}
};
// check if email is included in the invalid email addresses array
export const isEmailIncludedInInvalid = (email: string) => {
	const emailArray = [...state.InvalidEmailAddresses];
	if (emailArray?.includes(email)) {
		logger.debug(
			`${namespace}EmailValidator`,
			`Invalid email address already included: ${email}`,
		);
		return true;
	}
	return false;
};

// crate arrow function checking if title is valid
export const isTitleValid = (title: string) => {
	if (!title || title === "") {
		logger.debug(
			`${namespace}TitleValidator`,
			`isTitleValid: ${title} is invalid`,
		);
		return false;
	}
	logger.debug(`${namespace}TitleValidator`, `isTitleValid: ${title} is valid`);
	return true;
};

// create arrow function checking if first name is valid
export const isFirstNameValid = (firstName: string) => {
	if (
		firstName.length > 1 &&
		firstName.length < 20 &&
		/^[a-zA-Z'"-.\s]+$/.test(firstName)
	) {
		logger.debug(
			`${namespace}FirstNameValidator`,
			`isFirstNameValid: ${firstName} is valid`,
		);
		return true;
	}
	logger.debug(
		`${namespace}FirstNameValidator`,
		`isFirstNameValid: ${firstName} is invalid`,
	);
	return false;
};

// create arrow function checking if last name is valid
export const isLastNameValid = (lastName: string) => {
	// regex test for a-zA-Z and space and apostrophe
	if (
		lastName.length > 1 &&
		lastName.length < 20 &&
		/^[a-zA-Z'"-.\s]+$/.test(lastName)
	) {
		logger.debug(
			`${namespace}LastNameValidator`,
			`isLastNameValid: ${lastName} is valid`,
		);
		return true;
	}
	logger.debug(
		`${namespace}LastNameValidator`,
		`isLastNameValid: ${lastName} is invalid`,
	);
	return false;
};

// create arrow function checking if relationship is valid
export const isRelationshipValid = (relationship: string) => {
	if (relationship.length > 1 && relationship.length < 30) {
		logger.debug(
			`${namespace}RelationshipValidator`,
			`isRelationshipValid: ${relationship} is valid`,
		);
		return true;
	}
	logger.debug(
		`${namespace}RelationshipValidator`,
		`isRelationshipValid: ${relationship} is invalid`,
	);
	return false;
};

// create arrow function checking if number is valid
export const isNumberValid = (number: string) => {
	if (parseInt(number) > 0) {
		logger.debug(
			`${namespace}NumberValidator`,
			`isNumberValid: ${number} is valid`,
		);
		return true;
	}
	logger.debug(
		`${namespace}NumberValidator`,
		`isNumberValid: ${number} is invalid`,
	);
	return false;
};

// create arrow function checking if number is valid for property details
export const isNumberValidPropertyDetails = (number: string) => {
	if (parseInt(number) > -1) {
		logger.debug(
			`${namespace}NumberValidator`,
			`isNumberValid: ${number} is valid`,
		);
		return true;
	}
	logger.debug(
		`${namespace}NumberValidator`,
		`isNumberValid: ${number} is invalid`,
	);
	return false;
};

// create arrow function checking if date is valid
export const isDateValid = (date: string) => {
	if (date.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
		logger.debug(`${namespace}DateValidator`, `isDateValid: ${date} is valid`);
		return true;
	}
	logger.debug(`${namespace}DateValidator`, `isDateValid: ${date} is invalid`);
	return false;
};

// create arrow function checking if boiler model is valid
export const isBoilerModelValid = (boilerModel: string) => {
	if (boilerModel.length > 1 && boilerModel.length < 30) {
		logger.debug(
			`${namespace}BoilerModelValidator`,
			`isBoilerModelValid: ${boilerModel} is valid`,
		);
		return true;
	}
	logger.debug(
		`${namespace}BoilerModelValidator`,
		`isBoilerModelValid: ${boilerModel} is invalid`,
	);
	return false;
};

// create arrow function checking if boiler manufacturers is valid
export const isBoilerManufacturerValid = (boilerManufacturer: any) => {
	if (boilerManufacturer?.boilerManufacturerId > -1) {
		logger.debug(
			`${namespace}BoilerManufacturerValidator`,
			`isBoilerManufacturerValid: is valid`,
			boilerManufacturer,
		);
		return true;
	}
	logger.debug(
		`${namespace}BoilerManufacturerValidator`,
		`isBoilerManufacturerValid: is invalid`,
		boilerManufacturer,
	);
	return false;
};

// create arrow function checking if payment type is valid
export const isPaymentTypeValid = (paymentType: any) => {
	if (paymentType?.serviceAgreementPaymentTypeId) {
		logger.debug(
			`${namespace}PaymentTypeValidator`,
			`isPaymentTypeValid: is valid`,
			paymentType,
		);
		return true;
	}
	logger.debug(
		`${namespace}PaymentTypeValidator`,
		`isPaymentTypeValid: is invalid`,
		paymentType,
	);
	return false;
};

// create arrow function checking if payment frequency is valid
export const isPaymentFrequencyValid = (paymentFrequency: any) => {
	if (paymentFrequency?.id) {
		logger.debug(
			`${namespace}PaymentFrequencyValidator`,
			`isPaymentFrequencyValid: is valid`,
			paymentFrequency,
		);
		return true;
	}
	logger.debug(
		`${namespace}PaymentFrequencyValidator`,
		`isPaymentFrequencyValid: is invalid`,
		paymentFrequency,
	);
	return false;
};

// create arrow function checking if sort code is valid
export const isSortCodeValid = (sortCode: string) => {
	if (sortCode.length === 6 && /^\d{6}$/.test(sortCode)) {
		logger.debug(
			`${namespace}SortCodeValidator`,
			`isSortCodeValid: ${sortCode} is valid`,
		);
		return true;
	}
	logger.debug(
		`${namespace}SortCodeValidator`,
		`isSortCodeValid: ${sortCode} is invalid`,
	);
	return false;
};

// create arrow function checking if account number is valid
export const isAccountNumberValid = (accountNumber: string) => {
	if (accountNumber.length === 8 && /^\d{8}$/.test(accountNumber)) {
		logger.debug(
			`${namespace}AccountNumberValidator`,
			`isAccountNumberValid: ${accountNumber} is valid`,
		);
		return true;
	}
	logger.debug(
		`${namespace}AccountNumberValidator`,
		`isAccountNumberValid: ${accountNumber} is invalid`,
	);
	return false;
};

// create arrow function checking if payment method is valid
export const isPaymentMethodValid = (paymentMethod: any) => {
	if (paymentMethod?.id) {
		logger.debug(
			`${namespace}PaymentMethodValidator`,
			`isPaymentMethodValid: is valid`,
			paymentMethod,
		);
		return true;
	}
	logger.debug(
		`${namespace}PaymentMethodValidator`,
		`isPaymentMethodValid: is invalid`,
		paymentMethod,
	);
	return false;
};

// create arrow function checking if buildingTypes is valid
export const isBuildingTypesValid = (buildingTypes: any) => {
	if (buildingTypes?.propertyBuildingTypeId > 0) {
		logger.debug(
			`${namespace}BuildingTypesValidator`,
			`isBuildingTypesValid: is valid`,
			buildingTypes,
		);
		return true;
	}
	logger.debug(
		`${namespace}BuildingTypesValidator`,
		`isBuildingTypesValid: is invalid`,
		buildingTypes,
	);
	return false;
};

// create arrow function checking if payment requisition is valid
export const isPaymentRequisitionValid = (paymentRequisitionName: string) => {
	if (paymentRequisitionName) {
		logger.debug(
			`${namespace}PaymentRequisitionValidator`,
			`isPaymentRequisitionValid`,
			paymentRequisitionName,
		);
		if (paymentRequisitionName === "E-Mail" && ui.PropertyContactEmail === "") {
			ui.BookingPaymentMethodPaymentRequisitionsHelperText =
				"Please provide email address in contact details in order to create a payment requisition";
			return false;
		}
		ui.BookingPaymentMethodPaymentRequisitionsHelperText = "";
		return true;
	}
	logger.debug(
		`${namespace}PaymentRequisitionValidator`,
		`isPaymentRequisitionValid: id`,
		paymentRequisitionName,
	);
	return false;
};

// create arrow function checking if company registration is valid
export const isCompanyRegistrationValid = (companyRegistration: string) => {
	if (/^(SC|NI|OC|[0-9]{2})[0-9]{6}$/.test(companyRegistration)) {
		logger.debug(
			`${namespace}CompanyRegistrationValidator`,
			`isCompanyRegistrationValid: ${companyRegistration} is valid`,
		);
		return true;
	}
	logger.debug(
		`${namespace}CompanyRegistrationValidator`,
		`isCompanyRegistrationValid: ${companyRegistration} is invalid`,
	);
	return false;
};

export const isPolicyPlanTypeValid = (policyPlanType: any) => {
	if (policyPlanType?.serviceAgreementTypeId > 0) {
		logger.debug(
			`${namespace}PolicyPlanTypeValidator`,
			`isPolicyPlanTypeValid: is valid`,
			policyPlanType,
		);
		return true;
	}
	logger.debug(
		`${namespace}PolicyPlanTypeValidator`,
		`isPolicyPlanTypeValid: is invalid`,
		policyPlanType,
	);
	return false;
};

export const isPolicyDurationValid = (policyDuration: any) => {
	if (policyDuration?.serviceAgreementDurationId > 0) {
		logger.debug(
			`${namespace}PolicyPlanTypeValidator`,
			`isPolicyDurationValid: is valid`,
			policyDuration,
		);
		return true;
	}
	logger.debug(
		`${namespace}PolicyPlanTypeValidator`,
		`isPolicyDurationValid: is invalid`,
		policyDuration,
	);
	return false;
};

export const isPolicyBillingFrequencyValid = (
	policyBillingFrequency: any,
	prefix: Prefix,
) => {
	if (policyBillingFrequency?.serviceAgreementBillingFrequencyId > 0) {
		logger.debug(
			`${namespace}PolicyPlanTypeValidator`,
			`isPolicyBillingFrequencyValid: is valid`,
			policyBillingFrequency,
		);
		return true;
	}
	logger.debug(
		`${namespace}PolicyPlanTypeValidator`,
		`isPolicyBillingFrequencyValid: is invalid`,
		policyBillingFrequency,
	);
	return false;
};

export const isPolicyExcessValid = (policyExcess: any, prefix: Prefix) => {
	if (policyExcess?.serviceAgreementExcessId >= 0) {
		logger.debug(
			`${namespace}PolicyPlanTypeValidator`,
			`isPolicyExcessValid: is valid`,
			policyExcess,
		);
		return true;
	}
	logger.debug(
		`${namespace}PolicyPlanTypeValidator`,
		`isPolicyExcessValid: is invalid`,
		policyExcess,
	);
	return false;
};

export const isPolicyPlanOfferValid = (
	policyPlanOffer: any,
	prefix: Prefix,
) => {
	if (policyPlanOffer?.serviceAgreementOfferId > 0) {
		logger.debug(
			`${namespace}PolicyPlanOfferValidator`,
			`isPolicyPlanOfferValid: is valid`,
			policyPlanOffer,
		);
		return true;
	}
	logger.debug(
		`${namespace}PolicyPlanOfferValidator`,
		`isPolicyPlanOfferValid: is invalid`,
		policyPlanOffer,
	);
	return false;
};

export const isCCPromoCodeValid = (promoCode: string) => {
	if (
		temp.AvailablePromoCodes?.find((code) => code.campaignCode === promoCode)
	) {
		logger.debug(
			`${namespace}CCPromoCodeValidator`,
			`isCCPromoCodeValid: is valid`,
			promoCode,
		);
		return true;
	}
	logger.debug(
		`${namespace}CCPromoCodeValidator`,
		`isCCPromoCodeValid: is invalid`,
		promoCode,
	);
	return false;
};

export const isPolicyPlanOfferReasonValid = (
	policyPlanOfferReason: any,
	prefix: Prefix,
) => {
	if (policyPlanOfferReason?.serviceAgreementOfferAllocationReasonId > 0) {
		logger.debug(
			`${namespace}PolicyPlanOfferReasonValidator`,
			`isPolicyPlanOfferReasonValid: is valid`,
			policyPlanOfferReason,
		);
		return true;
	}
	logger.debug(
		`${namespace}PolicyPlanOfferReasonValidator`,
		`isPolicyPlanOfferReasonValid: is invalid`,
		policyPlanOfferReason,
	);
	return false;
};

// create arrow function checking if phone number or mobile number is valid
export const isPhoneNumberValid = (
	phoneNumber: string,
	prefix: ContactPrefix,
) => {
	phoneNumber = sanitizePhoneNumber(phoneNumber);
	if (/^0([1-6][0-9]{8,10}|7[0-9]{9})$/.test(phoneNumber)) {
		// if phone number second digit is 7 it is a mobile number
		if (phoneNumber.charAt(1) === "7") {
			logger.debug(
				`${namespace}PhoneNumberValidator`,
				`isPhoneNumberValid: ${phoneNumber} is a valid mobile number`,
			);
			state[`${prefix}MobileValid`] = true;
		}
		// otherwise it is a landline number
		else {
			logger.debug(
				`${namespace}PhoneNumberValidator`,
				`isPhoneNumberValid: ${phoneNumber} is a valid landline number`,
			);
			state[`${prefix}LandlineValid`] = true;
		}
		return true;
	}
	// if phone number second digit is 7 it is a mobile number
	if (phoneNumber.charAt(1) === "7") {
		logger.debug(
			`${namespace}PhoneNumberValidator`,
			`isPhoneNumberValid: ${phoneNumber} is an invalid mobile number`,
		);
		state[`${prefix}MobileValid`] = false;
	}
	// otherwise it is a landline number
	else {
		logger.debug(
			`${namespace}PhoneNumberValidator`,
			`isPhoneNumberValid: ${phoneNumber} is an invalid landline number`,
		);
		state[`${prefix}LandlineValid`] = false;
	}
	return false;
};

// sanitize phone number
export const sanitizePhoneNumber = (phoneNumber: string) => {
	phoneNumber = phoneNumber.replace(/\D+/g, "");
	phoneNumber = phoneNumber.replace(/\s/g, "");
	phoneNumber = phoneNumber.replace(/\+/g, "");
	phoneNumber = phoneNumber.replace(/\(/g, "");
	phoneNumber = phoneNumber.replace(/\)/g, "");
	phoneNumber = phoneNumber.replace(/-/g, "");
	phoneNumber = phoneNumber.replace(/\//g, "");
	return phoneNumber;
};

// sanitize text input, return only text, no numbers or special characters
export const sanitizeText = (text: string) => {
	// text = text.replace(/\s/g, "");
	text = text.replace(/\+/g, "");
	text = text.replace(/\(/g, "");
	text = text.replace(/\)/g, "");
	text = text.replace(/-/g, "");
	text = text.replace(/\//g, "");
	text = text.replace(/\d/g, "");
	return text;
};

// sanitize first name and last name
export const sanitizeName = (name: string) => {
	name = name.replace(/\+/g, ""); // remove all plus signs
	name = name.replace(/\(/g, ""); // remove all left parentheses
	name = name.replace(/\)/g, ""); // remove all right parentheses
	name = name.replace(/\//g, ""); // remove all forward slashes
	name = name.replace(/\d/g, ""); // remove all numbers
	name = name.replaceAll("  ", " "); // remove all spaces
	return name;
};

// sanitize first name
export const sanitizeFirstName = (firstName: string) => {
	firstName = sanitizeName(firstName);
	firstName = capitalize(firstName);
	return firstName;
};

// sanitize last name
export const sanitizeLastName = (lastName: string) => {
	lastName = sanitizeName(lastName);
	lastName = capitalize(lastName);
	return lastName;
};

// sanitize email
export const sanitizeEmail = (email: string) => {
	email = email.replace(/\s/g, "");
	email = email.toLowerCase();
	return email;
};

// sanitize business name
export const sanitizeBusinessName = (businessName: string) => {
	businessName = capitalize(businessName);
	return businessName;
};

// sanitize city
export const sanitizeCity = (city: string) => {
	city = city.toUpperCase();
	city = sanitizeText(city);
	return city;
};

// sanitize street
export const sanitizeStreet = (street: string) => {
	street = capitalize(street);
	return street;
};

// sanitize relationship
export const sanitizeRelationship = (relationship: string) => {
	relationship = capitalize(relationship);
	return relationship;
};

// sanitize sort code
export const sanitizeSortCode = (sortCode: string) => {
	sortCode = sortCode.replace(/\D+/g, "");
	sortCode = sortCode.length > 6 ? sortCode.substring(0, 6) : sortCode;
	return sortCode;
};

// sanitize account number
export const sanitizeAccountNumber = (accountNumber: string) => {
	accountNumber = accountNumber.replace(/\D+/g, "");
	accountNumber =
		accountNumber.length > 8 ? accountNumber.substring(0, 8) : accountNumber;
	return accountNumber;
};

// sanitize company registration
export const sanitizeCompanyRegistration = (companyRegistration: string) => {
	//If starts with SC or NI or OC add 2 to allowed number of characters
	const allowedLength =
		companyRegistration.startsWith("GB") ||
		companyRegistration.startsWith("SC") ||
		companyRegistration.startsWith("NI") ||
		companyRegistration.startsWith("OC")
			? 11
			: 9;
	companyRegistration =
		companyRegistration.length > allowedLength
			? companyRegistration.substring(0, allowedLength)
			: companyRegistration;
	return companyRegistration;
};

// sanitize postcode
export const sanitizePostcode = (postcode: string) => {
	postcode = postcode.toUpperCase();
	return postcode;
};

//sanitize promo code, capitalise and remove spaces
export const sanitizePromoCode = (promoCode: string) => {
	promoCode = promoCode.toUpperCase();
	promoCode = promoCode.replace(/\s/g, "");
	return promoCode;
};

export interface InitialStageState {
	[key: number]: {
		id: number;
		title: string;
		complete: boolean;
		label: string;
		description: string;
	};
}

// get index of highest active step from state using the switch statement or return length of state
// returns the index of the highest active step in the state or the length of the state if no active step is found
export const getActiveStep = (state: InitialStageState) => {
	const stateValues = Object.values(state);
	// findIndex returns -1 if not found
	const activeStep = stateValues.findIndex((step) => step.complete === false);
	return activeStep === -1 ? stateValues.length : activeStep;
};

// create arrow function checking if property number or name is valid
export const isPropertyValid = (property: string, prefix: Prefix) => {
	// is valid if longer than 1 character or has a digit
	if (property.length > 0 || /\d/.test(property)) {
		// use returnTrueResponse to return true
		return returnTrueResponse("property", prefix, property);
	}
	// use returnFalseResponse to return false
	return returnFalseResponse("property", prefix, property);
};

// create arrow function checking if street is valid
export const isStreetValid = (street: string, prefix: Prefix) => {
	// create regex for street name, it can include dots, apostrophes and spaces
	const streetRegex = /^[a-zA-Z0-9\s-'".]*$/;
	// create validator for street name
	const streetValidator = streetRegex.test(street);
	// create validator name for street name
	if (street.length > 1 && streetValidator) {
		// use returnTrueResponse to return true
		return returnTrueResponse("street", prefix, street);
	}
	// use returnFalseResponse to return false
	return returnFalseResponse("street", prefix, street);
};

// create arrow function checking if City is valid
export const isCityValid = (city: string, prefix: Prefix) => {
	if (city.length > 1) {
		// use returnTrueResponse to return true
		return returnTrueResponse("city", prefix, city);
	}
	// use returnFalseResponse to return false
	return returnFalseResponse("city", prefix, city);
};
// create arrow function checking if Country is valid
export const isCountryValid = (country: string, prefix: Prefix) => {
	if (country.length > 1) {
		// use returnTrueResponse to return true
		return returnTrueResponse("country", prefix, country);
	}
	// use returnFalseResponse to return false
	return returnFalseResponse("country", prefix, country);
};

// create arrow function checking if postcode is valid
export const isPostcodeValid = (postcode: string, prefix: Prefix) => {
	if (postcode.length > 1 && isPos(postcode)) {
		// use returnTrueResponse to return true
		return returnTrueResponse("postcode", prefix, postcode);
	}
	// use returnFalseResponse to return false
	return returnFalseResponse("postcode", prefix, postcode);
};

// create arrow function checking if business name is valid
export const isBusinessNameValid = (
	businessName: string,
	prefix: AddressPrefix,
) => {
	// check if business switch is off
	console.log("isBusinessNameValid", prefix, ui[`${prefix}IsBusiness`]);
	if (prefix === "CarePlanBillingAddress" || !ui[`${prefix}IsBusiness`])
		return true;
	if (businessName.length > 1) {
		// use returnTrueResponse to return true
		return returnTrueResponse("businessName", prefix, businessName);
	}
	// use returnFalseResponse to return false
	return returnFalseResponse("businessName", prefix, businessName);
};

// postcode validator
export const isPos = (postcode: string) => {
	return AddressUtils.validatePostcode(postcode);
};

// set false response and log info message and return false
export const returnFalseResponse = (
	name: string,
	prefix: Prefix,
	message: string,
) => {
	logger.debug(`${namespace}${name} Validator:`, message + " is invalid");
	return false;
};

// set true response and log info message and return true
export const returnTrueResponse = (
	name: string,
	prefix: Prefix,
	message: string,
) => {
	logger.debug(`${namespace}${name} Validator:`, message + " is valid");
	return true;
};
// capitalize first letter of name
export const capitalize = (name: string) => {
	return name.charAt(0).toUpperCase() + name.slice(1);
};

export const validateAddress = (
	prefix: AddressPrefix,
	name: AddressFields,
	value: string,
) => {
	// use switch statement to set valid
	// remove "Property" from name
	switch (name.toLowerCase()) {
		// if name is property
		case "property":
			// if flat is valid
			if (value && isPropertyValid(value, prefix)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;
		// if name is street
		case "street":
			// if street is valid
			if (value && isStreetValid(value, prefix)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;
		// if name is city
		case "city":
			// if city is valid
			if (value && isCityValid(value, prefix)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;
		// if name is country
		case "country":
			// if country is valid
			if (value && isCountryValid(value, prefix)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;
		// if name is postcode
		case "postcode":
			// if postcode is valid
			if (value && isPostcodeValid(value, prefix)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;
		// if name is business name
		case "businessname":
			// if business name is valid
			if (value && isBusinessNameValid(value, prefix)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;

		default:
			break;
	}
};
export const validateContact = (
	prefix: ContactPrefix,
	name: ContactFields | "Title" | "Relationship",
	value: string,
) => {
	let isValid = false;

	switch (name) {
		case "FirstName":
			isValid = !!value && isFirstNameValid(value);
			break;
		case "LastName":
			isValid = !!value && isLastNameValid(value);
			break;
		case "Email":
			isValid = !value || isEmailValidMinimal(value);
			break;
		case "Landline":
		case "Mobile":
			isValid = !!value && isPhoneNumberValid(value, prefix);
			break;
		case "Title":
			isValid = !!value && isTitleValid(value);
			break;
		case "Relationship":
			isValid = !!value && isRelationshipValid(value);
			break;
		default:
			return; // Optionally handle unknown cases or log an error
	}

	state[`${prefix}${name}Valid`] = isValid;
};

export const validateBoiler = (
	prefix: "CarePlanPlanDetailsBoiler",
	name: keyof CarePlanBoiler,
	value: string,
) => {
	// use switch statement to set valid
	// remove "Property" from name
	switch (name) {
		case "BoilerModel":
			// if boilermodel is valid
			if (value && isBoilerModelValid(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;
		case "BoilerManufacturers":
			// if boilermanufacturers is valid
			if (value && isBoilerManufacturerValid(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;
		case "BoilerInstallationDate":
			// if boilerinstallationdate is valid
			if (value && isDateValid(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;
		case "BoilerLastServicedDate":
			// if boilerlastserviceddate is valid
			if (value && isDateValid(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;
	}
};

export const validateProperty = (
	prefix: "CarePlanPlanDetailsProperty",
	name: keyof Omit<CarePlanPropertyDetails, "ExclusionDetails">,
	value: string,
) => {
	// use switch statement to set valid
	// remove "Property" from name
	switch (name) {
		case "Bathrooms":
			// if bathrooms is valid
			if (isNumberValidPropertyDetails(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;
		// if name is bedrooms
		case "Bedrooms":
			// if bedrooms is valid
			if (isNumberValidPropertyDetails(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;
		// if name is externaldoors
		case "ExternalDoors":
			// if externaldoors is valid
			if (isNumberValidPropertyDetails(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;
		// if name is gasappliances
		case "GasAppliances":
			// if gasappliances is valid
			if (isNumberValidPropertyDetails(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;
		// if name is radiators
		case "Radiators":
			// if radiators is valid
			if (isNumberValidPropertyDetails(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;
		// if name is gassafetyduedate
		case "GasSafetyDueDate":
			// if gassafetyduedate is valid
			if (value && isDateValid(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;
		// if name is buildingtypes
		case "BuildingTypes":
			// if buildingtypes is valid
			if (value && isBuildingTypesValid(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;

		default:
			break;
	}
};

export const validatePlanOptions = (
	prefix: "CarePlanPlanDetailsPlanOptions",
	name: keyof Omit<CarePlanPlanOptions, "Comments">,
	value: string,
) => {
	// use switch statement to set valid
	// remove "Property" from name
	switch (name) {
		case "PlanType":
			// if policyplantype is valid
			if (value && isPolicyPlanTypeValid(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;

		case "Duration":
			// if policyplantype is valid
			if (value && isPolicyDurationValid(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;

		case "BillingFrequency":
			// if policyplantype is valid
			if (value && isPolicyBillingFrequencyValid(value, prefix)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;

		case "Excess":
			// if policyplantype is valid
			if (value && isPolicyExcessValid(value, prefix)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;
	}
};

export const validateOffer = (
	prefix: "CarePlanPlanDetailsOffer",
	name: keyof CarePlanOffer,
	value: string,
) => {
	// use switch statement to set valid
	// remove "Property" from name
	switch (name) {
		case "OfferReason":
			// if policyofferreason is valid
			if (value && isPolicyPlanOfferReasonValid(value, prefix)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;

		case "OfferType":
			// if policyoffertype is valid
			if (value && isPolicyPlanOfferValid(value, prefix)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;

		case "PromoCode":
			// if policyoffertype is valid
			if (value && value.length > 0 && isCCPromoCodeValid(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;

		default:
			break;
	}
};

export const validateBilling = (
	prefix: "CarePlanBillingMethod",
	name: keyof CarePlanPaymentMethod,
	value: string,
) => {
	// use switch statement to set valid
	// remove "Property" from name
	switch (name) {
		case "PaymentType":
			// if paymenttypes is valid
			if (value && isPaymentTypeValid(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;

		default:
			break;
	}
};

export const validateBankDetails = (
	prefix: "CarePlanPaymentBankDetails",
	name: keyof CarePlanBankDetails,
	value: string,
) => {
	// use switch statement to set valid
	// remove "Property" from name
	switch (name) {
		case "SortCode":
			// if sortcode is valid
			if (value && isSortCodeValid(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;
		case "AccountNumber":
			// if accountnumber is valid
			if (value && isAccountNumberValid(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;

		case "BankName":
			// if accountnumber is valid
			if (value && value.length > 3) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;

		default:
			break;
	}
};

export const validateBookingPaymentMethod = (
	prefix: BookingPaymentMethodPrefix,
	name: keyof ReplaceStringInKeys<
		BookingPaymentMethodPaymentMethodUI,
		"BookingPaymentMethod",
		""
	>,
	value: string,
) => {
	// use switch statement to set valid
	// remove "Property" from name
	switch (name) {
		// if name is flat
		case "CardAgreed":
			// if cardagreed is valid
			if (value) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;

		case "CardSecured":
			// if cardsecured is valid
			if (value) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;

		case "CompanyRegistration":
			// if companyregistration is valid
			if (value && isCompanyRegistrationValid(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;

		case "PaymentRequisitions":
			// if paymentrequisitions is valid
			if (value && isPaymentRequisitionValid(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;

		case "PaymentMethods":
			// if paymentmethods is valid
			if (value && isPaymentMethodValid(value)) {
				// set valid to true
				state[`${prefix}${name}Valid`] = true;
			}
			// else set valid to false
			else {
				state[`${prefix}${name}Valid`] = false;
			}
			break;

		// else set valid to true
		default:
			break;
	}
};

// react hook to sanitize fields, accepts prefix, value and field name
export const sanitize = (
	value: string,
	name: string,
	skip: boolean = false,
) => {
	// set value to empty string
	// if value is not empty
	let sanitized = value;
	if (value !== "" && !skip) {
		// switch statement to sanitize fields
		switch (name.toLowerCase()) {
			// if name is email
			case "email":
				// set value to value sanitized
				sanitized = sanitizeEmail(value);
				break;
			// // if name is business name
			case "businessname":
				// set value to value sanitized
				sanitized = sanitizeBusinessName(value);
				break;
			// // if name is first name
			case "firstname":
				// set value to value sanitized
				sanitized = sanitizeFirstName(value);
				break;
			// // if name is last name
			case "lastname":
				// set value to value sanitized
				sanitized = sanitizeLastName(value);
				break;
			// // if name is title
			// case "title":
			//     // set value to value sanitized
			//     setValue(sanitizeTitle(value));
			//     break;
			// // if name is city
			case "city":
				// set value to value sanitized
				sanitized = sanitizeCity(value);
				break;
			// if name is street
			case "street":
				// set value to value sanitized
				sanitized = sanitizeStreet(value);
				break;

			// if name is postcode
			case "postcode":
				// set value to value sanitized
				sanitized = sanitizePostcode(value);
				break;
			// if name is landline
			case "landline":
				// set value to value sanitized
				sanitized = sanitizePhoneNumber(value);
				break;
			// if name is mobile
			case "mobile":
				// set value to value sanitized
				sanitized = sanitizePhoneNumber(value);
				break;
			// if name is relationship
			case "relationship":
				// set value to value sanitized
				sanitized = sanitizeRelationship(value);
				break;
			// if name is sort code
			case "sortcode":
				// set value to value sanitized
				sanitized = sanitizeSortCode(value);
				break;
			// if name is account number
			case "accountnumber":
				// set value to value sanitized
				sanitized = sanitizeAccountNumber(value);
				break;
			// if name is company registration
			case "companyregistration":
				// set value to value sanitized
				sanitized = sanitizeCompanyRegistration(value);
				break;

			case "promocode":
				// set value to value sanitized
				sanitized = sanitizePromoCode(value);
				break;

			// else set value to value
			default:
				sanitized = value;
				break;
		}
	}

	// log sanitized value
	logger.debug(
		`${namespace} useSanitizer: name: ${name}`,
		`sanitized: ${sanitized}`,
	);
	// return sanitized value
	return sanitized;
};
